/**
 * PropertySpecifications - Enhanced Property Details Display
 * 
 * Displays all available property fields including:
 * - Condition, aspect, view type
 * - Title type and zoning
 * - EPC/Energy rating
 * - Dynamic attributes (EAV values)
 */

'use client';

import type { Property, PropertyValueItem } from '@/lib/api/clients/properties';
import { 
  Home, 
  Compass, 
  Mountain, 
  FileText, 
  Building2, 
  Leaf, 
  Calendar,
  Info,
  CheckCircle,
  XCircle,
} from 'lucide-react';

export interface PropertySpecificationsProps {
  property: Property;
  className?: string;
  messages?: {
    title?: string;
    subtitle?: string;
    condition?: string;
    aspect?: string;
    viewType?: string;
    titleType?: string;
    zoning?: string;
    conservationArea?: string;
    epcRating?: string;
    yearBuilt?: string;
    yearRenovated?: string;
    additionalDetails?: string;
  };
}

// Human-readable labels for enum values
const conditionLabels: Record<string, string> = {
  NEW_CONSTRUCTION: 'New Construction',
  EXCELLENT: 'Excellent',
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  NEEDS_RENOVATION: 'Needs Renovation',
  UNDER_RENOVATION: 'Under Renovation',
  DEMOLITION: 'Demolition',
};

const aspectLabels: Record<string, string> = {
  NORTH: 'North',
  NORTH_EAST: 'North East',
  EAST: 'East',
  SOUTH_EAST: 'South East',
  SOUTH: 'South',
  SOUTH_WEST: 'South West',
  WEST: 'West',
  NORTH_WEST: 'North West',
  VARIABLE: 'Variable',
  MULTIPLE: 'Multiple Aspects',
};

const viewTypeLabels: Record<string, string> = {
  CITY_SKYLINE: 'City Skyline',
  WATER_OCEAN: 'Ocean View',
  WATER_LAKE: 'Lake View',
  WATER_RIVER: 'River View',
  MOUNTAIN: 'Mountain View',
  VALLEY: 'Valley View',
  PARK_GREEN_SPACE: 'Park/Green Space',
  GARDEN: 'Garden View',
  WOODED_FOREST: 'Forest View',
  GOLF_COURSE: 'Golf Course View',
  RURAL_COUNTRYSIDE: 'Countryside View',
  URBAN_STREETSCAPE: 'Urban View',
  LANDMARK: 'Landmark View',
  POOL_AREA: 'Pool View',
  COURTYARD: 'Courtyard View',
  LIMITED_OBSTRUCTED: 'Limited View',
  NONE: 'No View',
  PANORAMIC: 'Panoramic View',
  MULTIPLE: 'Multiple Views',
};

const titleTypeLabels: Record<string, string> = {
  FREEHOLD: 'Freehold',
  LEASEHOLD: 'Leasehold',
  COMMONHOLD: 'Commonhold',
  SHARE_FREEHOLD: 'Share of Freehold',
  STRATA: 'Strata Title',
  CONDOMINIUM: 'Condominium',
  COOPERATIVE: 'Cooperative',
  LAND_LEASE: 'Land Lease',
  UNKNOWN: 'Unknown',
  OTHER: 'Other',
};

const zoningLabels: Record<string, string> = {
  RESIDENTIAL_SINGLE_FAMILY: 'Residential (Single Family)',
  RESIDENTIAL_MULTI_FAMILY: 'Residential (Multi Family)',
  COMMERCIAL_RETAIL: 'Commercial (Retail)',
  COMMERCIAL_OFFICE: 'Commercial (Office)',
  COMMERCIAL_HOSPITALITY: 'Commercial (Hospitality)',
  INDUSTRIAL_LIGHT: 'Industrial (Light)',
  INDUSTRIAL_HEAVY: 'Industrial (Heavy)',
  MIXED_USE: 'Mixed Use',
  AGRICULTURAL: 'Agricultural',
  OPEN_SPACE: 'Open Space',
  PARKLAND: 'Parkland',
  CONSERVATION: 'Conservation',
  INSTITUTIONAL: 'Institutional',
  TRANSPORTATION: 'Transportation',
  SPECIAL_PURPOSE: 'Special Purpose',
  PLANNED_DEVELOPMENT: 'Planned Development',
  UNKNOWN: 'Unknown',
  OTHER: 'Other',
};

export function PropertySpecifications({
  property,
  className = '',
  messages = {},
}: PropertySpecificationsProps) {
  const {
    title = 'Property Specifications',
    subtitle = 'Detailed property information',
    condition = 'Condition',
    aspect = 'Aspect',
    viewType = 'View',
    titleType = 'Title Type',
    zoning = 'Zoning',
    conservationArea = 'Conservation Area',
    epcRating = 'Energy Rating',
    yearBuilt = 'Year Built',
    yearRenovated = 'Year Renovated',
    additionalDetails = 'Additional Details',
  } = messages;

  // Check if we have any specifications to show
  const hasSpecifications = 
    property.condition ||
    property.aspect ||
    property.viewType ||
    property.titleType ||
    property.zoningCategory ||
    property.conservationArea ||
    property.primaryCertificationRating ||
    property.yearBuilt ||
    property.yearRenovated;

  // Get dynamic attributes
  const dynamicAttributes = property.attributeValues 
    ? Object.values(property.attributeValues).filter(
        attr => attr.attributeCode !== 'ASKING_PRICE' && attr.attributeCode !== 'askingPrice'
      )
    : [];

  if (!hasSpecifications && dynamicAttributes.length === 0) {
    return null;
  }

  return (
    <section className={`${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Condition */}
        {property.condition && (
          <SpecItem
            icon={<Home className="w-5 h-5" />}
            label={condition}
            value={conditionLabels[property.condition] || property.condition}
          />
        )}

        {/* Aspect */}
        {property.aspect && (
          <SpecItem
            icon={<Compass className="w-5 h-5" />}
            label={aspect}
            value={aspectLabels[property.aspect] || property.aspect}
          />
        )}

        {/* View Type */}
        {property.viewType && (
          <SpecItem
            icon={<Mountain className="w-5 h-5" />}
            label={viewType}
            value={viewTypeLabels[property.viewType] || property.viewType}
          />
        )}

        {/* Title Type */}
        {property.titleType && (
          <SpecItem
            icon={<FileText className="w-5 h-5" />}
            label={titleType}
            value={titleTypeLabels[property.titleType] || property.titleType}
          />
        )}

        {/* Zoning */}
        {property.zoningCategory && (
          <SpecItem
            icon={<Building2 className="w-5 h-5" />}
            label={zoning}
            value={zoningLabels[property.zoningCategory] || property.zoningCategory}
          />
        )}

        {/* Conservation Area */}
        {property.conservationArea !== undefined && (
          <SpecItem
            icon={<Leaf className="w-5 h-5" />}
            label={conservationArea}
            value={property.conservationArea ? 'Yes' : 'No'}
            highlight={property.conservationArea}
          />
        )}

        {/* EPC Rating */}
        {property.primaryCertificationRating && (
          <SpecItem
            icon={<Leaf className="w-5 h-5" />}
            label={epcRating}
            value={property.primaryCertificationRating}
            badge
          />
        )}

        {/* Year Built */}
        {property.yearBuilt && (
          <SpecItem
            icon={<Calendar className="w-5 h-5" />}
            label={yearBuilt}
            value={property.yearBuilt.toString()}
          />
        )}

        {/* Year Renovated */}
        {property.yearRenovated && (
          <SpecItem
            icon={<Calendar className="w-5 h-5" />}
            label={yearRenovated}
            value={property.yearRenovated.toString()}
          />
        )}
      </div>

      {/* Dynamic Attributes */}
      {dynamicAttributes.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5" />
            {additionalDetails}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dynamicAttributes.map((attr) => (
              <DynamicAttributeItem key={attr.attributeId} attribute={attr} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

interface SpecItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  highlight?: boolean;
  badge?: boolean;
}

function SpecItem({ icon, label, value, highlight, badge }: SpecItemProps) {
  return (
    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
      <div className="text-primary mt-0.5">{icon}</div>
      <div className="flex-1">
        <p className="text-sm text-gray-500">{label}</p>
        {badge ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800 mt-1">
            {value}
          </span>
        ) : highlight !== undefined ? (
          <div className="flex items-center gap-1 mt-1">
            {highlight ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <XCircle className="w-4 h-4 text-gray-400" />
            )}
            <span className="font-medium text-gray-900">{value}</span>
          </div>
        ) : (
          <p className="font-medium text-gray-900 mt-0.5">{value}</p>
        )}
      </div>
    </div>
  );
}

interface DynamicAttributeItemProps {
  attribute: PropertyValueItem;
}

function DynamicAttributeItem({ attribute }: DynamicAttributeItemProps) {
  // Determine the display value based on value type
  let displayValue: string | React.ReactNode = '-';
  
  if (attribute.valueString) {
    displayValue = attribute.valueString;
  } else if (attribute.valueNumber !== null && attribute.valueNumber !== undefined) {
    displayValue = attribute.valueNumber.toLocaleString();
  } else if (attribute.valueBoolean !== null && attribute.valueBoolean !== undefined) {
    displayValue = (
      <span className="flex items-center gap-1">
        {attribute.valueBoolean ? (
          <>
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Yes</span>
          </>
        ) : (
          <>
            <XCircle className="w-4 h-4 text-gray-400" />
            <span>No</span>
          </>
        )}
      </span>
    );
  } else if (attribute.valueText) {
    displayValue = attribute.valueText.length > 100 
      ? `${attribute.valueText.slice(0, 100)}...` 
      : attribute.valueText;
  } else if (attribute.valueDate) {
    displayValue = new Date(attribute.valueDate).toLocaleDateString();
  } else if (attribute.valueStringArray && attribute.valueStringArray.length > 0) {
    displayValue = attribute.valueStringArray.join(', ');
  }

  return (
    <div className="p-3 bg-white border border-gray-200 rounded-lg">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{attribute.attributeName}</p>
      <p className="font-medium text-gray-900 mt-1">{displayValue}</p>
    </div>
  );
}

export default PropertySpecifications;
